import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop'
import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core'
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { ActivatedRoute, Router } from '@angular/router'
import { BadgeEditorService } from '@app/modules/admin/components/badge-editor/services/badge-editor.service'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { EventService } from '@app/modules/events/services/event.service'
import { LegalSetup } from '@app/modules/events/types/legal-setup.type'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { DialogData } from '@app/modules/shared/components/dialog/dialog.interface'
import { DTCMDialog } from '@app/modules/shared/components/dialog/dtcm.dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { Checkin } from '@app/v2/event/types/event.type'
import { Zone as V2Zone } from '@app/v2/event/types/zone.type'
import { PersonaBadgePreviewService } from '@app/v2/personas/services/persona-badge-preview.service'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { environment } from '@environments/environment'
import { ClientRepresentedEvent } from '@events/models/event.model'
import { Zone } from '@events/models/zone.model'
import { NbAccordionComponent, NbAccordionItemComponent, NbAccordionModule, NbActionsModule, NbButtonModule, NbToggleModule } from '@nebular/theme'
import { DEFAULT_PERSONA, GENERIC_PERSONA, Persona, Personas, VISITOR_PERSONA } from '@personas/interfaces/persona.interface'
import { PromoCode } from '@personas/interfaces/promo-code.interface'
import { NameValueChangeHook } from '@personas/models/name.value-change.hook'
import { PersonaFactoryService } from '@personas/models/persona.model'
import { PersonaService } from '@personas/services/persona.service'
import { PromoCodeService } from '@personas/services/promo-code.service'
import { cloneDeep } from 'lodash'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators'
import { PersonaFieldTypes } from '../../enums/persona-fields-types.enum'
import { PersonaTypes } from '../../enums/persona-types.enum'
import { PersonaField } from '../../interfaces/persona-field.interface'
import { FormComponent } from '../form/form.component'
/**
 * Main component for persona editing in the event creation wizard
 */
@Component({
  selector: 'ngx-personas-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.scss'],
  imports: [CommonModule, FormsModule, NbAccordionModule, NbToggleModule, NbActionsModule, FormComponent, MatIconModule, NbButtonModule, ReactiveFormsModule],
  providers: [BadgeEditorService, PersonaBadgePreviewService]
})
export class EditComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public personaId: number = null
  /** When true, cancel/close does not navigate to legacy routes; emits {@link embeddedClose} instead. */
  @Input() public embeddedInV2Wizard = false
  @Output() public embeddedClose = new EventEmitter<void>()
  /**
   * Current event model
   */
  @Input()
  event: ClientRepresentedEvent
  /**
   * Sets the current personas and intializes the component
   */
  @Input()
  set personas(value: Personas) {
    this._personas = this._filterPersonasForEmbeddedEdit(value)
    // Don't initialize here if form doesn't exist yet - let ngOnInit handle it
    // This prevents initializing with empty array before Visitor persona is created
    if (this.form) {
      this.initialize()
    }
  }
  /**
   * Current workspace slug
   */
  @Input()
  workspaceSlug: any
  /**
   * Emits personas changes
   */
  @Output()
  personasChange: EventEmitter<any> = new EventEmitter<any>()
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()

  /**
   * Personas list
   */
  private _personas: Personas
  private _isLegalSetupConfigured = false
  private _eventCity: string = ''
  private _eventLegalSetup: LegalSetup = null
  /**
   * Whether the personas are being updated (Edit mode)
   */
  isUpdatePersona = false
  /**
   * Gets personas values array
   */
  get personas(): Personas {
    return this.form.value.forms as Personas
  }
  /**
   * Returns personas in form group as FormArray
   */
  get personasFormArray(): UntypedFormArray {
    return this.form.controls['forms'] as UntypedFormArray
  }

  /** v2 overlay: single persona editor (no accordion, bottom toolbar). */
  get embeddedSinglePersonaMode(): boolean {
    return this.embeddedInV2Wizard && this.personaId != null
  }

  /** Index of the persona row shown in {@link embeddedSinglePersonaMode}. */
  get embeddedDisplayIndex(): number {
    if (this._embeddedFocusIndex != null && this._embeddedFocusIndex < (this.personasFormArray?.length ?? 0)) {
      return this._embeddedFocusIndex
    }
    const activePersonaId = this._activePersonaIdFromInputOrRoute()
    if (activePersonaId == null) {
      return 0
    }
    const targetId = Number(activePersonaId)
    const values = this.personasFormArray?.value as { id?: number | string }[]
    for (let i = 0; i < (values?.length ?? 0); i++) {
      const rowId = values[i]?.id
      if (rowId != null && !Number.isNaN(targetId) && Number(rowId) === targetId) {
        return i
      }
    }
    return 0
  }

  /**
   * Personas form group
   */
  form: UntypedFormGroup
  /**
   * Deleted personas ids
   */
  deletedPersonas = []
  /**
   * List of available promo codes for the event
   */
  promoCodes: PromoCode[] | []

  /**
   * is the personas is valid
   */
  isValidForms = true

  /**
   * first time to open the component
   */

  isInitFirstTime = true

  /**
   * register count of every persona
   */
  personasRegisrationsCount: { form: string; count: number }[]

  isLoading = false

  /** When set (e.g. after clone in v2 overlay), overrides {@link personaId} for which row is shown. */
  private _embeddedFocusIndex: number | null = null

  get eventLegalSetup(): LegalSetup {
    if (this._eventLegalSetup) return this._eventLegalSetup
    const cityLower = this._eventCity.toLowerCase()
    // Check if city contains "abu dhabi" for DCT
    if (cityLower.includes('abu dhabi')) {
      this._eventLegalSetup = 'DCT'
      return this._eventLegalSetup
    }
    // Check if city contains "dubai" for DTCM
    if (cityLower.includes('dubai')) {
      this._eventLegalSetup = 'DTCM'
      return this._eventLegalSetup
    }
    this._eventLegalSetup = 'NONE'
    return this._eventLegalSetup
  }

  /**
   * Creates the component and injects it's dependencies
   * @param fb FormBuilder
   * @param personaService PersonaService
   * @param pf PersonaFactory
   * @param toastr ToastrService
   * @param cdr ChangeDetectorRef
   * @param router Router
   * @param route ActivatedRoute
   * @param promoCodeService PromoCodeService
   * @param dialogService NbDialogService
   * @param auth AuthService
   */
  constructor(
    private fb: UntypedFormBuilder,
    private personaService: PersonaService,
    private pf: PersonaFactoryService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private promoCodeService: PromoCodeService,
    private auth: AuthService,
    private dialog: MatDialog,
    private eventService: EventService,
    private alertService: AlertService,
    private badgeEditorService: BadgeEditorService,
    private personaBadgePreviewService: PersonaBadgePreviewService,
    private wizardService: WizardService
  ) {}
  /**
   * Initializes the component
   */
  ngOnInit() {
    /**
     * Register conversion hooks
     */
    this.pf.registerGlobalValueChangeHook('name', new NameValueChangeHook(this.pf))
    this._eventCity = (this.event?.checkins?.find((checkin: any) => checkin.slug === 'global')?.location_details?.venue_city ?? '') as string
    const addPersona = this.route.snapshot.params?.personaId ?? null
    if (!addPersona && !this.embeddedInV2Wizard) {
      const visitorPersona = cloneDeep(VISITOR_PERSONA)
      visitorPersona.sub_locations_ids = this.getSubLocationIds()
      visitorPersona.email_subject = this.event.name.length > 66 ? `Confirmation: ${this.event.name.substring(0, 63)}...` : `Confirmation: ${this.event.name}`
      visitorPersona.email_from_name = this.event.name.length > 80 ? `${this.event.name.substring(0, 77)}...` : `${this.event.name}`
      visitorPersona.email_reply = 'no-reply@micetribe.com'
      // Ensure we have at least a Visitor persona when there are no personas or less than 2
      const hasPersonas = this._personas && this._personas.length > 0
      const needsVisitorPersona = !hasPersonas || this._personas.length < 2

      if (this.eventLegalSetup !== 'NONE') {
        if (needsVisitorPersona) {
          this._personas = [this._updateLegalFieldsRequirement(visitorPersona)]
        } else {
          this._personas = this._personas.map((persona) => (persona.type === PersonaTypes.GENERIC ? persona : this._updateLegalFieldsRequirement(persona)))
        }
      } else {
        if (needsVisitorPersona) {
          this._personas = [visitorPersona]
        }
      }
    }

    // Initialize form synchronously so it exists when template renders
    // This prevents the "formGroup expects a FormGroup instance" error
    if (!this.form) {
      this.form = this.fb.group({
        forms: this.pf.generateFormArray(this._personas, this.event?.id, this.event?.physical_type_id, this.getSubLocationIds(), this.event.name)
      })
    }
    this._applyDefaultSubLocationIdsForEmbedded()

    this.promoCodeService
      .getPromoCodes(this.event?.id?.toString())
      .pipe(takeUntil(this._destroy$))
      .subscribe((o: any[]) => {
        this.promoCodes = o?.length ? (o as PromoCode[]) : []
        // Form is already created above, just ensure it's up to date with latest personas
        this.initialize()
      })
    this.personaService.isValidPersonas$.pipe(takeUntil(this._destroy$)).subscribe((res: boolean) => {
      this.isValidForms = res
    })
    switch (this.eventLegalSetup) {
      case 'DTCM':
        this._isLegalSetupConfigured = this._personas.some((form) => form.type !== PersonaTypes.GENERIC && form.options.dtcm?.eventCode)
        break
      case 'DCT':
        this._isLegalSetupConfigured = this._personas.some((form) => form.type !== PersonaTypes.GENERIC && form.options.dct?.eventId)
        break
      default:
        this._isLegalSetupConfigured = false
        break
    }

    this.eventService
      .getRegistrationCount(this.event.slug)
      .pipe(takeUntil(this._destroy$))
      .subscribe((res) => (this.personasRegisrationsCount = res))
  }

  /** On destroy implementation */
  ngOnDestroy(): void {
    this.wizardService.setHeaderActions(null)
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Initializes the component forms
   */
  initialize() {
    if (!this.form) {
      this.form = this.fb.group({
        forms: this.pf.generateFormArray(this._personas, this.event?.id, this.event?.physical_type_id, this.getSubLocationIds(), this.event.name)
      })
    } else {
      this.form.controls['forms'].patchValue(this._personas)
    }
    this._applyDefaultSubLocationIdsForEmbedded()
    this._scheduleOpenAccordionForSelectedPersona()
    this._syncWizardHeaderActions()
  }

  /**
   * Saves and updates/ deletes all personas
   * @returns Observable
   */
  save() {
    if (this.isLoading) return
    if (!this.event) {
      this.toastr.warning('Heads up!', 'Kindly setup your event info & location first!')
      return
    }
    if (!this.event.physical_type_id) {
      this.toastr.warning('Heads up!', 'Kindly setup your location first!')
      return
    }
    const formArray = this.form.controls.forms['controls']
    const form = formArray.find((formGroup: UntypedFormGroup) => formGroup.controls.enable_payment.value && +formGroup.controls.registration_amount.value < 1)
    if (form) {
      this.toastr.error('Heads up!', `Kindly add a valid ticket price for ${form?.controls?.name?.value} persona!`)
      return
    }
    if (!this.form.valid) {
      this.displayErrors()
      return
    }
    // Generate slugs names
    this.personaService.generateNewPersonaSlugs(this.form.controls['forms'])
    this._applyDefaultSubLocationIdsForEmbedded()
    this._ensureEventVenueOnPersonas()
    if (!this.embeddedInV2Wizard) {
      const inNeedOfZones = this.personas.filter((o) => !o.sub_locations_ids || !o.sub_locations_ids.length).map((o) => o.name)
      if (inNeedOfZones.length) {
        this.toastr.warning('Select your persona zones!', 'You need to select at least one zone for the following personas: ' + inNeedOfZones.join(', '), 5000)
        return
      }
    }
    this.personas.forEach((persona) => {
      if (persona.enable_payment && !persona.registration_amount) persona.registration_amount = 0
      if (!persona.enable_payment) delete persona.registration_amount
      persona =
        persona.enable_payment && persona.registration_amount > 0 && persona.require_approval
          ? this.addPreApprovalPriceMessage(persona)
          : this.removePreApprovalPriceMessage(persona)
      this.updateSuccessAndRegretMessage(persona)
      delete persona.promo_codes
    })
    const personasToSave = this.embeddedSinglePersonaMode ? [this.personas[this.embeddedDisplayIndex]] : this.personas
    const request = this.pf.toRequest(personasToSave, this.event)
    const organizationId = (this.auth.currentParentWorkspace || this.auth.currentWorkspace)?.id

    this.isLoading = true
    this._syncWizardHeaderActions()
    return this.personaService.publishAndUpdateFormsWithCommunication(organizationId, this.event.id, request.create, request.update).pipe(
      map(({ created, updated }) => {
        const filtered = []
        created.forEach((persona) => {
          const exists = !!filtered.find((o) => o.id === persona.id)
          persona.checkins = persona.checkinIds.map((o) => {
            return {
              id: o
            }
          })
          if (!exists && !!persona.id) {
            filtered.push(persona)
          }
        })
        updated.forEach((persona) => {
          const exists = !!filtered.find((o) => o.id === persona.id)
          persona.checkins = persona.checkinIds.map((o) => {
            return {
              id: o
            }
          })
          if (!exists && !!persona.id) {
            filtered.push(persona)
          }
        })
        return filtered
      }),
      takeUntil(this._destroy$)
    )
  }

  /**
   * Update persona success and regret messages to have inline styles
   * @param persona Persona
   */
  updateSuccessAndRegretMessage(persona) {
    if (persona.success_message) persona.success_message = this.updateMessageStyles(persona.success_message)
    if (persona.regret_message) persona.regret_message = this.updateMessageStyles(persona.regret_message)
  }

  private updateMessageStyles(message: string): string {
    const parser = new DOMParser()
    const doc = parser.parseFromString(message, 'text/html')
    const images = doc.querySelectorAll('img')
    images.forEach((image) => (image.style.maxWidth = '100%'))

    const headers = doc.querySelectorAll('h1,h2,h3,h4,h5,h6')
    headers.forEach((header: HTMLElement) => (header.style.lineHeight = '1em'))

    const blockquotes = doc.querySelectorAll('blockquote')
    blockquotes.forEach((blockquote) => {
      blockquote.style.borderLeft = '4px solid #ccc'
      blockquote.style.paddingLeft = '16px'
      blockquote.style.marginTop = '5px'
      blockquote.style.marginBottom = '5px'
      blockquote.style.fontWeight = 'bold'
    })

    const lists = doc.querySelectorAll('ul,ol')
    lists.forEach((list: HTMLElement) => (list.style.paddingRight = '1.5em'))

    const listItems = doc.querySelectorAll('ul li')
    listItems.forEach((li: HTMLElement) => (li.style.listStyleType = 'disc'))

    return doc.body.innerHTML
  }

  /**
   * Display form errors
   */

  displayErrors() {
    const errors = []
    const formArray = this.form.controls['forms']['controls']
    formArray.forEach((formGroup: UntypedFormGroup) => {
      if (formGroup.valid) return
      if (formGroup['controls']['name'].invalid) {
        this.toastr.warning('Heads up!', 'Persona title is missing')
        return
      }

      const formEmail = {
        sender_name: formGroup['controls']['email_from_name'].invalid ? true : false,
        reply_to: formGroup['controls']['email_reply'].invalid ? true : false,
        email_subject: formGroup['controls']['email_subject'].invalid ? true : false
      }
      const formEmailErrorMessage = 'Kindly review'
      const formEmailErrors = []
      for (const key in formEmail) {
        if (formEmail[key]) formEmailErrors.push(key)
      }
      if (formEmailErrors.length) {
        this.toastr.warning('Heads up!', `${formEmailErrorMessage} ${formEmailErrors.join(', ')} email fields in Messages & Info in ${formGroup.value.name}`)
        return
      }

      const formElements = formGroup['controls']['form_elements']['controls']
      const fields = []
      formElements.forEach((element: UntypedFormGroup) => {
        if (element.valid) return
        fields.push(element.value.name)
      })
      errors.push({ persona: formGroup.value.name, fields })
    })
    if (errors.length) {
      let errorsString = 'Kindly review'
      errors.forEach((error: { persona: string; fields: string[] }) => {
        const invalidFields = error.fields.join(', ')
        errorsString += ` ${error.persona} Persona, ${invalidFields} field${error.fields.length > 1 ? 's' : ''}!`
      })
      this.toastr.warning('Heads up!', errorsString)
    }
  }

  /**
   * Delete personas if within deletedPersonas
   * @returns void
   */
  checkDeletePersona() {
    if (this.deletedPersonas.length) {
      this.personaService
        .deleteForms(this.deletedPersonas)
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {})
    }
  }

  /**
   * Save personas when user click to Finish & Share button
   * @returns void
   */
  finish(showShareable: { status: boolean; formSlug: any; index?: number } | null = null) {
    const persona = this.personas.find((persona) => {
      return this.checkElementHasOption(persona)
    })
    if (persona) {
      this.toastr.error('error', `Missing options for a select element in ${persona.name} form`)
      return
    }

    // Check for invalid displayIf conditions (fields selected without values)
    const personaWithInvalidDisplayIf = this.personas.find((persona) => {
      return this._checkInvalidConditions(persona, 'displayIf')
    })
    if (personaWithInvalidDisplayIf) {
      this.alertService.alert(Alert.ERROR, `Display If condition is missing a value in ${personaWithInvalidDisplayIf.name} form`)
      return
    }

    // Check for invalid requiredIf conditions (fields selected without values)
    const personaWithInvalidRequiredIf = this.personas.find((persona) => {
      return this._checkInvalidConditions(persona, 'requiredIf')
    })
    if (personaWithInvalidRequiredIf) {
      this.alertService.alert(Alert.ERROR, `Required If condition is missing a value in ${personaWithInvalidRequiredIf.name} form`)
      return
    }
    const showLegalServiceDialog = this.eventLegalSetup !== 'NONE' && !this._isLegalSetupConfigured
    const save$ = this.save()
    if (!save$) return

    save$
      .pipe(
        switchMap((savedPersonas: Personas) => this._syncBadgesAfterFormsSave$(savedPersonas).pipe(map(() => savedPersonas))),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (personas: Personas) => {
          this._emitFinishSuccess(personas, showShareable, showLegalServiceDialog)
        },
        error: () => {
          this.isLoading = false
          this._syncWizardHeaderActions()
        }
      })
    this.checkDeletePersona()
  }

  /**
   * After PUT forms, GET badge HTML, sync field keys / remove stale slots, PATCH badges/update when HTML changed.
   * @param savedPersonas {Personas} - Personas to sync badges for.
   * @returns {Observable<void>} - Observable of void.
   */
  private _syncBadgesAfterFormsSave$(savedPersonas: Personas): Observable<void> {
    const { savedBySlug, savedById } = this._indexSavedPersonas(savedPersonas)

    this._patchPersonasFormArrayWithSavedPersonas(savedPersonas)

    const personasToSync = this._getPersonasToSyncBadges()
    if (!personasToSync.length) return of(void 0)

    const updates = this._collectBadgeHtmlUpdates(personasToSync, savedBySlug, savedById)

    if (!updates.length) return of(void 0)

    return this._applyBadgeUpdates$(updates)
  }

  /**
   * Index saved personas by slug and id
   * @param savedPersonas {Personas} - Saved personas to index.
   * @returns {Object} - Object with savedBySlug and savedById maps.
   */
  private _indexSavedPersonas(savedPersonas: Personas): {
    savedBySlug: Map<string, Personas[number]>
    savedById: Map<number, Personas[number]>
  } {
    const savedBySlug = new Map<string, Personas[number]>()
    const savedById = new Map<number, Personas[number]>()

    savedPersonas.forEach((p) => {
      if (p.slug) savedBySlug.set(p.slug, p)
      savedById.set(p.id, p)
    })

    return { savedBySlug, savedById }
  }

  /**
   * Patch personas form array with saved personas
   * @param savedPersonas {Personas} - Saved personas to patch form array with.
   */
  private _patchPersonasFormArrayWithSavedPersonas(savedPersonas: Personas): void {
    savedPersonas.forEach((saved) => {
      const formIndex = this._findPersonaFormIndexInArray(saved)
      if (formIndex === -1) return

      const patch: Record<string, unknown> = { id: saved.id }
      if (saved.form_elements?.length) {
        patch.form_elements = [...saved.form_elements].sort((a, b) => (a.weight > b.weight ? 1 : -1))
      }
      this.personasFormArray.at(formIndex).patchValue(patch)
    })
  }

  /**
   * Find persona form index in array
   * @param saved {Personas[number]} - Saved persona to find form index for.
   * @returns {number} - Form index.
   */
  private _findPersonaFormIndexInArray(saved: Personas[number]): number {
    let formIndex = this.personasFormArray.controls.findIndex((c) => c.value.slug === saved.slug)
    if (formIndex === -1) {
      formIndex = this.personasFormArray.controls.findIndex((c) => c.value.id === saved.id)
    }
    return formIndex
  }

  /**
   * Get personas to sync badges
   * @returns {Personas} - Personas to sync badges for.
   */
  private _getPersonasToSyncBadges(): Personas {
    return this.personas.filter((p) => p?.id) as Personas
  }

  /**
   * Collect badge HTML updates
   * @param personasToSync {Personas} - Personas to sync badges for.
   * @param savedBySlug {Map<string, Personas[number]>} - Saved personas by slug.
   * @param savedById {Map<number, Personas[number]>} - Saved personas by id.
   * @returns {Array<{ form_id: number; badge_front_html_content: string }>} - Updates to apply.
   */
  private _collectBadgeHtmlUpdates(
    personasToSync: Personas,
    savedBySlug: Map<string, Personas[number]>,
    savedById: Map<number, Personas[number]>
  ): Array<{ form_id: number; badge_front_html_content: string }> {
    const updates: Array<{ form_id: number; badge_front_html_content: string }> = []

    personasToSync.forEach((persona) => {
      const saved = (persona.slug && savedBySlug.get(persona.slug)) ?? savedById.get(persona.id)
      if (!saved) return

      const html = saved?.badge_front_html_content
      if (!html || typeof html !== 'string') return

      const updatedHtml = this._syncBadgeHtmlFromFormElements(persona, saved, html)
      if (!updatedHtml || updatedHtml === html) return

      updates.push({ form_id: persona.id, badge_front_html_content: updatedHtml })
    })

    return updates
  }

  /**
   * Sync badge HTML from form elements
   * @param persona {Personas[number]} - Persona to sync badge HTML for.
   * @param saved {Personas[number]} - Saved persona to sync badge HTML for.
   * @param html {string} - HTML to sync badge HTML for.
   * @returns {string | null} - Synced badge HTML, or null if sync fails.
   */
  private _syncBadgeHtmlFromFormElements(persona: Personas[number], saved: Personas[number], html: string): string | null {
    const personaForSync = { ...persona, ...saved, form_elements: saved.form_elements ?? persona.form_elements }
    const updated = this.personaBadgePreviewService.syncBadgeHtmlFromFormElements(personaForSync as Persona, html)
    if (!updated || typeof updated !== 'string') return null
    return updated
  }

  /**
   * Apply badge updates to the database
   * @param updates {Array<{ form_id: number; badge_front_html_content: string }>} - Updates to apply.
   * @returns {Observable<void>} - Observable of void.
   */
  private _applyBadgeUpdates$(updates: Array<{ form_id: number; badge_front_html_content: string }>): Observable<void> {
    // Update badge per persona (badges/update)
    return forkJoin(
      updates.map((u) =>
        this.badgeEditorService.updateBadge({
          form_id: u.form_id,
          badge_front_html_content: u.badge_front_html_content,
          reset: false
        })
      )
    ).pipe(
      map(() => void 0),
      catchError(() => of(void 0))
    )
  }

  /**
   * Emit finish success event
   * @param personas {Personas} - Personas to emit finish success event for.
   * @param showShareable {Object} - Show shareable object.
   * @param showLegalServiceDialog {boolean} - Show legal service dialog.
   * @returns {void}
   */
  private _emitFinishSuccess(personas: Personas, showShareable: { status: boolean; formSlug?: string; index?: number } | null, showLegalServiceDialog: boolean): void {
    const personasToShare = this.embeddedSinglePersonaMode ? [this.personas[this.embeddedDisplayIndex]] : this.personas
    this.personasChange.emit({ personas: personasToShare, showShareable: (!showShareable || showShareable.status) && !showLegalServiceDialog })
    if (showLegalServiceDialog && (!showShareable || showShareable.status)) {
      this._openLegalServiceDialog()
    }
    if (!showShareable || showShareable.status) {
      this.toastr.success('Great!', 'Personas are saved!')
    } else {
      // Prefer formSlug — save() returns a rebuilt create/update list that does not preserve form indices
      const formSlug =
        showShareable.formSlug || (typeof showShareable.index === 'number' ? personas[showShareable.index]?.slug || this.personas[showShareable.index]?.slug : undefined)
      const workspaceSlug = this.auth.currentWorkspace?.slug
      const eventSlug = this.event?.slug
      if (!formSlug || !workspaceSlug || !eventSlug) {
        this.toastr.error('Heads up!', 'Unable to open confirmation preview.')
      } else {
        const confirmationPreviewSrc = `${environment.publicFormsUrl}workspaces/${workspaceSlug}/events/${eventSlug}/forms/${formSlug}/confirmation`
        window.open(confirmationPreviewSrc, '_blank')
      }
    }
    this.isLoading = false
    this._syncWizardHeaderActions()
  }

  checkElementHasOption(persona) {
    return (
      persona.form_elements.some((element) => element.form_element_type_id === PersonaFieldTypes.CHECKBOX && element.form_element_values.length === 0) ||
      persona.form_elements.some((element) => element.form_element_type_id === PersonaFieldTypes.RADIO && element.form_element_values.length === 0) ||
      persona.form_elements.some((element) => element.form_element_type_id === PersonaFieldTypes.GENDER && element.form_element_values.length === 0)
    )
  }

  private _fixSuccessMessageHyperLink(persona) {
    if (persona.success_message) {
      const fixedSuccessMessage = persona.success_message.replace(/"unsafe:/g, '"https://')
      return fixedSuccessMessage
    }
    return ''
  }

  /**
   * Checks if any form element has conditions (displayIf or requiredIf) with missing values
   * @param persona Persona to check
   * @param conditionType 'displayIf' or 'requiredIf'
   * @returns true if there are invalid conditions
   */
  private _checkInvalidConditions(persona, conditionType: 'displayIf' | 'requiredIf'): boolean {
    return persona.form_elements.some((element) => {
      const conditionData = element.options?.[conditionType]
      if (!conditionData) return false

      // Normalize to array
      const conditions = Array.isArray(conditionData) ? conditionData : [conditionData]

      // Check if any condition has a field but empty value
      return conditions.some((condition) => {
        if (!condition.field) return false
        const value = condition.value
        const isEmpty = Array.isArray(value) ? value.length === 0 : !value || (typeof value === 'string' && value.trim() === '')
        return isEmpty
      })
    })
  }

  /**
   * Gets a list of zones ids
   * @returns number[]
   */
  getSubLocationIds(): any {
    if (!this.event) {
      return []
    }
    const physicalTypeId = this.event.physical_type_id
    if (physicalTypeId == null) {
      return []
    }
    const zones = this.event.zones
    if (!zones) {
      return []
    }
    const sublocationsIds: number[] = []
    if (physicalTypeId === 1) {
      const physical = zones.physical
      if (!physical?.id) {
        return []
      }
      sublocationsIds.push(physical.id)
      this._zoneSubcheckins(physical).forEach((subcheckin) => sublocationsIds.push(subcheckin.id))
    } else if (physicalTypeId === 2) {
      const virtual = zones.virtual
      if (!virtual) {
        return []
      }
      this._zoneSubcheckins(virtual).forEach((subcheckin) => sublocationsIds.push(subcheckin.id))
    } else {
      const physical = zones.physical
      const virtual = zones.virtual
      if (physical?.id) {
        sublocationsIds.push(physical.id)
        this._zoneSubcheckins(physical).forEach((subcheckin) => sublocationsIds.push(subcheckin.id))
      }
      if (virtual) {
        this._zoneSubcheckins(virtual).forEach((subcheckin) => sublocationsIds.push(subcheckin.id))
      }
    }
    return sublocationsIds
  }

  /**
   * When opened from the v2 overlay, pre-fill empty persona zone selections with all legacy zone/subzone ids
   * so save matches the old wizard behaviour. If the API exposes no zones, {@link embeddedInV2Wizard} save skips the zone gate.
   * Single pass O(P) over persona form rows (no nested loops).
   */
  private _applyDefaultSubLocationIdsForEmbedded(): void {
    if (!this.embeddedInV2Wizard || !this.form) {
      return
    }
    const defaults = this.getSubLocationIds().filter((id: number) => id != null)
    if (!defaults.length) {
      return
    }
    this.personasFormArray.controls.forEach((ctrl: UntypedFormGroup) => {
      const c = ctrl.get('sub_locations_ids')
      if (!c) return
      const raw = c.value as number[] | null | undefined
      if (Array.isArray(raw) && raw.length > 0) return
      c.patchValue([...defaults])
    })
  }

  /**
   * Event Venue (slug `global`) is required on every persona.
   */
  private _ensureEventVenueOnPersonas(): void {
    const globalId = this.availableZones.find((zone) => zone.slug === 'global')?.id
    if (globalId == null || !this.form) return
    this.personasFormArray.controls.forEach((ctrl: UntypedFormGroup) => {
      const c = ctrl.get('sub_locations_ids')
      if (!c) return
      const raw = (c.value as Array<number | string>) ?? []
      if (raw.some((id) => String(id) === String(globalId))) return
      c.patchValue([...raw, globalId])
    })
  }

  /**
   * Gets a list of promo codes ids
   * @returns number[]
   */
  getPromoCodesIds(): any {
    if (!this.event || !this.promoCodes) {
      return []
    }
    const promoCodesIds = []
    this.promoCodes.forEach((o: PromoCode) => promoCodesIds.push(o.id))

    return promoCodesIds
  }

  /**
   * Adds a new default persona to the personas list
   */
  addPersona() {
    const defaultPersonaCloned = cloneDeep(DEFAULT_PERSONA)
    let defaultPersona = this.eventLegalSetup !== 'NONE' ? this._updateLegalFieldsRequirement(defaultPersonaCloned) : defaultPersonaCloned

    // When legal setup is NONE, ensure country label is "Country" not "Nationality"
    if (this.eventLegalSetup === 'NONE') {
      defaultPersona = this._revertCountryLabel(defaultPersona)
    }

    const persona = this.pf.generateFormGroup(
      defaultPersona,
      this.event?.physical_type_id,
      this.getSubLocationIds(),
      this.event?.id,
      this.personasFormArray.length,
      this.personasFormArray.value,
      this.event.name,
      true,
      this.personasFormArray
    )
    persona.controls.sub_locations_ids.patchValue(
      (this.event.checkins ?? []).filter((checkin) => !!checkin.parent && checkin.slug != 'global_virtual' && !this._isSnoballZone(checkin)).map((checkin) => checkin.id)
    )
    this.personasFormArray.push(persona)
    this.cdr.detectChanges()
    this.components.toArray()[this.components.length - 1].open()
    this.personaService.isValidPersonas$.next(false)
  }

  private _updateLegalFieldsRequirement(persona: Persona) {
    let hasCountryField = false
    persona.form_elements.forEach((element) => {
      if ([PersonaFieldTypes.COUNTRY, PersonaFieldTypes.EMAIL, PersonaFieldTypes.PHONE, PersonaFieldTypes.FULLNAME].includes(element.form_element_type_id)) {
        element.is_required = 3
      }
      if (element.form_element_type_id === PersonaFieldTypes.COUNTRY && element.name === 'country') {
        element.label = 'Nationality'
        hasCountryField = true
      }
    })

    // If the COUNTRY field does not exist, add it
    if (!hasCountryField) {
      const maxWeight = persona.form_elements.length

      persona.form_elements.push({
        form_element_type_id: PersonaFieldTypes.COUNTRY,
        label: 'Nationality',
        weight: maxWeight + 1,
        name: 'country',
        is_required: 3,
        is_unique: false,
        is_disable_online: false,
        is_disable_onsite: false,
        form_element_values: []
      })
    }

    return persona
  }

  /**
   * Reverts country field label to "Country" when legal setup is NONE
   * This ensures that when location changes from DTCM/DCT to NONE, newly added personas have the correct label
   */
  private _revertCountryLabel(persona: Persona): Persona {
    persona.form_elements.forEach((element) => {
      if (element.form_element_type_id === PersonaFieldTypes.COUNTRY && element.name === 'country') {
        element.label = 'Country'
      }
    })
    return persona
  }

  /**
   * Moves the persona in the personas list
   * @param event CdkDragDrop
   */
  movePersona(event: CdkDragDrop<UntypedFormGroup>) {
    if (this.personasFormArray) {
      const _personas = this.personas
      moveItemInArray(_personas, event.previousIndex, event.currentIndex)
      moveItemInArray(this.personasFormArray.controls, event.previousIndex, event.currentIndex)
      this.pf.generatePersonasWeights(_personas)
      this.personas = _personas
    }
  }
  /**
   * Returns the persona as a minified version
   * @param o Persona
   * @returns any
   */
  minifyPersona(o: Persona) {
    return {
      name: o.name,
      slug: o.slug,
      weight: o.weight,
      fields: o.form_elements.map((f) => {
        return {
          label: f.label,
          name: f.name,
          weight: f.weight
        }
      })
    }
  }
  /**
   * Returns the personas as a minified version
   */
  get personasMinified() {
    return this.personas.map((o) => this.minifyPersona(o))
  }

  /**
   * Saves the persona individually
   * @deprecated
   * @param index number
   */
  saveProgress(index: number) {
    // if (!this.savingItems.find((o) => o.index === index)) {
    //   this.savingItems.push({ index: index, status: 'saving' })
    // }
    // const savingItem = this.savingItems.find((o) => o.index === index)
    // const value = { ...this.pf.toRequest(this.personas)[index] }
    // const req$: Observable<any> = this.personaService.publishForms(value['event_id'], [value])
    // req$.pipe(debounceTime(1000)).subscribe(
    //   (data) => {
    //     savingItem.status = 'saved'
    //     if (!value.id) {
    //       this.personasFormArray.controls[index].patchValue({ id: data[0]['id'] })
    //     }
    //   },
    //   () => {
    //     savingItem.status = 'notYet'
    //   }
    // )
  }

  /**
   * Closes the editor and navigates back to event timeline
   */
  close() {
    if (this.embeddedInV2Wizard) {
      this.embeddedClose.emit()
      return
    }
    this.router.navigateByUrl('events/list')
  }

  /**
   * NbAccordionItem view children
   */
  @ViewChildren('personasRef') components: QueryList<NbAccordionItemComponent>
  /**
   * NbAccordion view child
   */
  @ViewChild('personaAccordionRef') accordion: NbAccordionComponent

  /**
   * Initializes the component after rendering
   * @returns void
   */
  ngAfterViewInit() {
    if (this.embeddedSinglePersonaMode) {
      this.isUpdatePersona = true
      const ctrl = this.personasFormArray?.controls[this.embeddedDisplayIndex]
      ctrl?.['controls']?.opened?.setValue(true)
      this._bindEmbeddedHeaderActionListeners()
      this._syncWizardHeaderActions()
      return
    }
    const activePersonaId = this._activePersonaIdFromInputOrRoute()
    activePersonaId ? (this.isUpdatePersona = true) : (this.isUpdatePersona = false)
    if (activePersonaId === 'new') {
      this.addPersona()
      this.cdr.detectChanges()
      this.components.toArray()[this.personasFormArray.length - 1].open()
      return
    }
    if (!this._openAccordionForSelectedPersona(activePersonaId)) {
      requestAnimationFrame(() => this._openAccordionForSelectedPersona(activePersonaId))
    }
  }

  /**
   * Persona to focus from {@link personaId} input or legacy route param.
   */
  private _activePersonaIdFromInputOrRoute(): string | number | null {
    if (this.personaId != null) {
      return this.personaId
    }
    return this.route.snapshot.params?.personaId ?? null
  }

  /** v2 overlay: only the persona opened from the listing (API still returns all forms). */
  private _filterPersonasForEmbeddedEdit(personas: Personas): Personas {
    if (!this.embeddedInV2Wizard || this.personaId == null || !personas?.length) {
      return personas
    }
    const filtered = personas.filter((p) => p.id === this.personaId)
    return filtered.length ? filtered : personas
  }

  /** Max deferred tries after DOM updates; avoids unbounded timers (no infinite retries). */
  private static readonly _ACCORDION_OPEN_MAX_ATTEMPTS = 3

  /**
   * After {@link initialize} patchValue (e.g. promo codes), accordion items are recreated — open the selected persona again.
   * Bounded O(1) scheduling: at most {@link _ACCORDION_OPEN_MAX_ATTEMPTS} microtask/rAF steps; opening each attempt is O(P) for P personas.
   */
  private _scheduleOpenAccordionForSelectedPersona(): void {
    if (this.embeddedSinglePersonaMode) {
      return
    }
    const activePersonaId = this._activePersonaIdFromInputOrRoute()
    if (activePersonaId == null || activePersonaId === 'new') {
      return
    }
    let attempt = 0
    const run = () => {
      if (attempt >= EditComponent._ACCORDION_OPEN_MAX_ATTEMPTS) {
        return
      }
      attempt++
      if (this._openAccordionForSelectedPersona(activePersonaId)) {
        return
      }
      requestAnimationFrame(run)
    }
    queueMicrotask(run)
  }

  /**
   * Expands the accordion for the active persona id (or single persona when id is absent).
   * @returns true if done (opened, nothing to open, or unknown id); false if accordion rows are not ready yet.
   * Complexity: O(P) for P persona rows — one {@link QueryList#toArray}, one linear scan of form values.
   */
  private _openAccordionForSelectedPersona(activePersonaId: string | number | null): boolean {
    const items = this.components?.toArray() ?? []
    const formLen = this.personasFormArray?.length ?? 0
    if (!items.length || !formLen) {
      return false
    }
    if (!activePersonaId && items.length === 1) {
      const personaIndex = formLen - 1
      this.personasFormArray.controls[personaIndex]['controls'].opened.setValue(true)
      items[personaIndex].open()
      this.cdr.detectChanges()
      return true
    }
    const targetId = Number(activePersonaId)
    const values = this.personasFormArray.value as { id?: number | string }[]
    let indexOfActivePersona = -1
    for (let i = 0; i < values.length; i++) {
      const rowId = values[i]?.id
      if (rowId != null && !Number.isNaN(targetId) && Number(rowId) === targetId) {
        indexOfActivePersona = i
        break
      }
    }
    if (indexOfActivePersona === -1) {
      return !(activePersonaId != null && activePersonaId !== 'new' && formLen > 0 && items.length > 0)
    }
    this.cdr.detectChanges()
    const row = items[indexOfActivePersona]
    row?.open()
    this.personasFormArray.controls[indexOfActivePersona]?.['controls']?.opened?.setValue(true)
    return true
  }
  /**
   * Gets the persona title
   * @param persona FormGroup
   * @returns string
   */
  title(persona, index: number) {
    if (persona.value['name']) {
      return persona.value['name']
    }
    // First persona should default to "Visitor", others to "New persona"
    return index === 0 ? 'Visitor' : 'New persona'
  }

  /**
   * Show the delete confiration ref
   * @param i number
   * @param event
   */
  showDeletePersonaDialog(i: number, event) {
    event.preventDefault()
    event.stopPropagation()
    const registrations = this.personasRegisrationsCount.find((persona) => persona.form === this.personas[i].slug)?.count
    const dialogData: DialogData = {
      headerText: registrations ? `Warning: The ${this.personas[i].name} persona already has associated registrants!` : `Are you sure you want to delete ${this.personas[i].name}?`,
      messageText: registrations
        ? `Please delete all registrations to enable deleting this persona.`
        : `Deleting the ${this.personas[i].name} persona will remove all associated field configurations, confirmations, reminders, and the landing page.`,
      cancelButtonText: registrations ? 'Ok' : 'Cancel',
      cancelButtonClass: registrations ? 'btn-primary' : 'cancel',
      confirmButtonText: 'Delete',
      confirmButtonClass: registrations ? 'd-none' : 'delete'
    }
    const dialogRef = this.dialog.open(DialogComponent, {
      minWidth: '544px',
      panelClass: 'matdialog',
      data: dialogData
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result) {
          if ((this.personasFormArray.get(i.toString()).value as Persona).id) this.deletedPersonas.push((this.personasFormArray.get(i.toString()).value as Persona).id)
          this.personasFormArray.removeAt(i)
          if (this.embeddedSinglePersonaMode) {
            this.checkDeletePersona()
            this.embeddedClose.emit()
            return
          }
          this.personas = [...(this.personasFormArray.value as Personas), GENERIC_PERSONA]
          this.initialize()
          const invalidPersona = this.personas.find((persona) => persona.name === '' || persona.name === undefined || persona.name === null)
          this.personaService.isValidPersonas$.next(invalidPersona ? false : true)
        }
      })
  }
  /**
   * Clones the current persona and adds it to the group
   * @param index number
   * @param event MouseEvent
   */
  clonePersona(index: number, event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
    const persona = this.personasFormArray.get(index.toString()).value as Persona
    // Set the persona type to 10(normal persona) as a default value
    persona.type = 10
    persona.form_email = { email_subject: persona.email_subject, email_from_name: persona.email_from_name, email_reply: persona.email_reply }
    const generatedPersona = this.pf.generateFormGroup(
      { ...persona, name: persona.name + ' Copy', id: null },
      this.event?.physical_type_id,
      this.getSubLocationIds(),
      this.event?.id,
      this.personas.length,
      this.personas,
      this.getPromoCodesIds(),
      false,
      null,
      persona.landing_page_options
    )
    generatedPersona.controls.sub_locations_ids.patchValue(persona.sub_locations_ids)
    this.personasFormArray.push(generatedPersona)
    this.cdr.detectChanges()
    if (this.embeddedSinglePersonaMode) {
      this._embeddedFocusIndex = this.personasFormArray.length - 1
      this.personasFormArray.controls[this._embeddedFocusIndex]?.['controls']?.opened?.setValue(true)
      this._syncWizardHeaderActions()
      return
    }
    this.components.toArray().forEach((accordion) => accordion.close())
    this.components.toArray()[this.personas.length - 1].open()
  }

  /**
   * Persona accordion collapsed/ expanded
   * @param isOpening boolean
   * @param index number
   */
  collapsedChange(index: number) {
    this.personasFormArray.controls.forEach((o) => o['controls'].opened.setValue(false))
    this.personasFormArray.controls[index]['controls'].opened.setValue(true)
  }

  /**
   * If the persona is in edit mode (already created)
   * @param index number
   * @returns boolean
   */
  isEdited(index: number) {
    return !!this.personasFormArray.value[index]['id']
  }

  /**
   * Gets a list of errors for the current form
   * @param form FormGroup
   * @param location FormArray
   * @returns string[]
   */
  calculateErrors(form: UntypedFormGroup | UntypedFormArray, location = '') {
    let errors = []
    Object.keys(form.controls).forEach((field) => {
      const control = form.get(field)
      if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
        errors = errors.concat(this.calculateErrors(control, field ? location + '.' + field : location))
        return
      }

      const controlErrors: ValidationErrors = control.errors
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach((keyError) => {
          errors.push({
            controlName: location ? location + '.' + field : field,
            errorName: keyError,
            errorValue: controlErrors[keyError]
          })
        })
      }
    })

    // This removes duplicates
    errors = errors.filter(
      (error, index, self) =>
        self.findIndex((t) => {
          return t.controlName === error.controlName && t.errorName === error.errorName
        }) === index
    )
    return errors
  }
  /**
   * If the persona is valid
   */
  get isValid(): boolean {
    if (!this.form) {
      return false
    }
    this.form.updateValueAndValidity()
    return this.form.valid
  }
  /**
   * Returns a list of available zones as id/name array
   */
  get availableZones(): V2Zone[] {
    if (this.event?.checkins?.length) return this.checkinsToIdNameArray(this.event.checkins as Checkin[])

    if (!this.event?.zones) {
      return []
    }
    switch (this.event.physical_type_id) {
      case 1:
        return this.zoneToIdNameArray(this.event.zones.physical)
      case 2:
        return this.zoneToIdNameArray(this.event.zones.virtual)
      case 3:
        return [...this.zoneToIdNameArray(this.event.zones.physical), ...this.zoneToIdNameArray(this.event.zones.virtual)]
      default:
        return []
    }
  }

  /**
   * Snöball is an event app stored on a checkin, so it must not appear as persona zone access.
   * @param zone Checkin or zone row.
   * @returns {boolean}
   */
  private _isSnoballZone(zone: { slug?: string; location_details?: unknown }): boolean {
    const service = (zone?.location_details as { service?: { slug?: string } } | undefined)?.service
    return zone?.slug === 'snoball' || service?.slug === 'snoball'
  }

  /**
   * Subcheckins that can be assigned as persona zones.
   * @param zone Parent zone.
   * @returns {Zone[]}
   */
  private _zoneSubcheckins(zone: Zone): Zone[] {
    return (zone.subcheckins ?? []).filter((subcheckin) => !this._isSnoballZone(subcheckin))
  }

  /**
   * Converts a list of checkins to an id/name array
   * @param checkins Checkin[]
   * @returns V2Zone[]
   */
  private checkinsToIdNameArray(checkins: Checkin[]): V2Zone[] {
    const all: V2Zone[] = []

    for (const checkin of checkins ?? []) {
      if (this._isSnoballZone(checkin)) continue
      if (Array.isArray(checkin.subcheckins) && checkin.subcheckins.length) {
        all.push(
          ...checkin.subcheckins
            .filter((subcheckin: Checkin) => !this._isSnoballZone(subcheckin))
            .map((subcheckin: Checkin) => ({
              id: subcheckin.id,
              name: subcheckin.name,
              slug: subcheckin.slug,
              parent: subcheckin.parent ?? checkin.id
            }))
        )
      } else {
        // Keep backward compatibility for flat payloads, but hide root checkin containers like "main".
        if (checkin?.slug === 'main' && !checkin?.parent) continue
        all.push({
          id: checkin.id,
          name: checkin.name,
          slug: checkin.slug,
          parent: checkin.parent ?? null
        })
      }
    }

    const seen = new Set<number>()
    return all.filter((zone: V2Zone) => {
      if (!zone?.id || seen.has(zone.id)) return false
      seen.add(zone.id)
      return true
    })
  }
  /**
   * Converts a zone and subzones into an id/ name array
   * @param zone zone
   * @returns any[]
   */
  private zoneToIdNameArray(zone: Zone) {
    if (!zone || this._isSnoballZone(zone)) {
      return []
    }
    const zones = [{ id: zone.id, name: zone.name, slug: zone.slug }]
    const children = zone.subcheckins
      ?.filter((subcheckin) => !this._isSnoballZone(subcheckin))
      .map((o) => {
        return {
          id: o.id,
          name: o.name,
          slug: o.slug,
          parent: o.parent
        }
      })
    if (children?.length) {
      zones.push(...children)
    }
    return zones
  }
  /**
   * Add warning message - if it doesn't exist - to a persona
   * @param persona Persona
   * @returns Persona
   */
  private addPreApprovalPriceMessage(persona: Persona) {
    let approvalPaymentMessageField: PersonaField = persona.form_elements.filter((o) => o.options?.approval_payment_message)[0]
    if (!approvalPaymentMessageField) {
      approvalPaymentMessageField = {
        form_element_type_id: PersonaFieldTypes.WARNING_MESSAGE,
        is_disable_online: false,
        is_disable_onsite: true,
        is_required: 0,
        is_unique: false,
        label: 'Please note upon approval of your application, you’ll receive a payment link to complete the process.',
        name: 'warn_line',
        options: { approval_payment_message: true },
        weight: 100
      }
      persona.form_elements.push(approvalPaymentMessageField)
    }
    return persona
  }
  /**
   * Remove warning message from a persona
   * @param persona Persona
   * @returns Persona
   */
  private removePreApprovalPriceMessage(persona: Persona) {
    persona.form_elements = persona.form_elements.filter((personaField) => !personaField.options?.approval_payment_message)
    return persona
  }

  nameChanged(ev) {
    this.personaService.isValidPersonas$.next(ev)
  }

  private _openLegalServiceDialog() {
    const dialogData: DialogData = {
      headerText: `Your event is in ${this._eventCity.charAt(0).toUpperCase() + this._eventCity.slice(1)}, Exciting!`,
      messageText: '',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Connect my event',
      confirmButtonClass: 'continue',
      city: this._eventCity
    }
    const dialogRef = this.dialog.open(DTCMDialog, {
      minWidth: '652px',
      panelClass: 'matdialog',
      data: dialogData
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        switch (result) {
          case 'DET':
            this.router.navigate([`/events/${this.event.id}/dtcm`])
            return
          case 'DCT':
            this.router.navigate([`/events/${this.event.id}/dct`])
            return
        }
        if (this.embeddedInV2Wizard) {
          this.embeddedClose.emit()
        } else {
          this.router.navigate(['/events/list'])
        }
      })
  }

  toggleMicetribeEvents($event, index) {
    const personas = (this.personasFormArray.value as Array<any>).filter((o) => o.show_in_events)
    if (personas.length === 1 && this.personasFormArray.value[index].show_in_events === true) {
      this.toastr.info('Heads up!', 'After saving your event will be added to micetribe events')
      setTimeout(() => {
        window.open(environment.micetribeEvents, '_blank')
      }, 2000)
    }
  }

  /** v2 overlay header: persist feature toggle without leaving the editor. */
  onEmbeddedFeatureToggle(enabled: boolean): void {
    const index = this.embeddedDisplayIndex
    const personaGroup = this.personasFormArray?.at(index) as UntypedFormGroup | undefined
    if (!personaGroup || !this.event?.id) return

    const previous = personaGroup.get('show_in_events')?.value ?? false
    personaGroup.get('show_in_events')?.setValue(enabled)

    const personaId = personaGroup.value?.id
    if (!personaId) {
      this.alertService.alert(Alert.SUCCESS, 'Preference saved')
      this._syncWizardHeaderActions()
      return
    }

    this.personaService
      .updateForm({
        id: personaId,
        event_id: this.event.id,
        show_in_events: enabled
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.alertService.alert(Alert.SUCCESS, enabled ? 'This persona will be featured on MICEtribe events.' : 'This persona will no longer be featured on MICEtribe events.')
          this._syncWizardHeaderActions()
        },
        error: () => {
          personaGroup.get('show_in_events')?.setValue(previous)
          this.alertService.alert(Alert.ERROR, 'Could not save your preference. Please try again.')
          this._syncWizardHeaderActions()
        }
      })
  }

  private _bindEmbeddedHeaderActionListeners(): void {
    const personaGroup = this.personasFormArray?.at(this.embeddedDisplayIndex) as UntypedFormGroup | undefined
    const control = personaGroup?.get('show_in_events')
    if (!control) return
    control.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this._syncWizardHeaderActions())
  }

  private _syncWizardHeaderActions(): void {
    if (!this.embeddedSinglePersonaMode) {
      this.wizardService.setHeaderActions(null)
      return
    }
    const index = this.embeddedDisplayIndex
    const personaGroup = this.personasFormArray?.at(index) as UntypedFormGroup | undefined
    if (!personaGroup) {
      this.wizardService.setHeaderActions(null)
      return
    }
    const type = personaGroup.get('type')?.value
    const showFeatureToggle = type !== 31 && type !== 30
    this.wizardService.setHeaderActions({
      showFeatureToggle,
      featureEnabled: personaGroup.get('show_in_events')?.value ?? false,
      onFeatureToggle: (enabled) => this.onEmbeddedFeatureToggle(enabled),
      showSave: true,
      saveLabel: 'Save',
      saveDisabled: this.isLoading,
      saveLoading: this.isLoading,
      onSave: () => this.finish()
    })
  }
}
